import React from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, ClipboardCheck, Settings, RefreshCw } from 'lucide-react';
import { useT } from '../i18n';

const STEPS = [
  {
    icon: MessageSquare,
    en: { title: 'Discovery — your way', body: 'Start with a short call, an email or WhatsApp — whatever suits you best. No pressure, no sales scripts.' },
    es: { title: 'Discovery — a tu manera', body: 'Empieza con una llamada corta, un email o WhatsApp — lo que prefieras. Sin presión, sin guiones de venta.' },
  },
  {
    icon: ClipboardCheck,
    en: { title: 'Free Audit', body: 'We analyze your visibility, operations and revenue opportunities — and send you a clear, honest report.' },
    es: { title: 'Auditoría gratis', body: 'Analizamos tu visibilidad, operaciones y oportunidades de ingresos — y te enviamos un reporte claro y honesto.' },
  },
  {
    icon: Settings,
    en: { title: 'Expert Setup', body: 'KITXENS configures your platform end to end. You never touch the technology.' },
    es: { title: 'Setup experto', body: 'KITXENS configura tu plataforma de principio a fin. Tú nunca tocas la tecnología.' },
  },
  {
    icon: RefreshCw,
    en: { title: 'Monthly Optimization', body: 'We monitor, optimize and report every month — a technology department that never stops improving.' },
    es: { title: 'Optimización mensual', body: 'Monitoreamos, optimizamos y reportamos cada mes — un departamento tecnológico que nunca deja de mejorar.' },
  },
];

/* Reusable 4-step onboarding strip — the Stripe/HubSpot "what happens next" pattern. */
export default function HowWeWork({ className = '' }) {
  const { lang } = useT();
  const isEs = lang === 'es';

  return (
    <section className={`py-16 lg:py-20 bg-neutral-50 border-y border-neutral-200 ${className}`} data-testid="how-we-work">
      <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="kx-eyebrow mb-4">{isEs ? 'CÓMO TRABAJAMOS CONTIGO' : 'HOW WE WORK WITH YOU'}</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 tracking-tight">
            {isEs ? 'Del primer contacto al crecimiento continuo.' : 'From first contact to continuous growth.'}
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const t = isEs ? s.es : s.en;
            return (
              <motion.div
                key={t.title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: i * 0.07 }}
                className="relative rounded-2xl border border-neutral-200 bg-white p-6 hover:border-[#F77F11]/50 hover:shadow-[0_20px_50px_-25px_rgba(247,127,17,0.35)] transition-all"
                data-testid={`hww-step-${i + 1}`}
              >
                <span className="absolute top-4 right-4 text-[12px] font-bold text-[#F77F11] bg-orange-50 border border-[#F77F11]/25 rounded-full px-2.5 py-1 leading-none select-none tabular-nums">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="mx-auto w-11 h-11 rounded-xl bg-neutral-950 text-white flex items-center justify-center">
                  <Icon className="w-5 h-5" />
                </span>
                <h3 className="mt-5 text-center font-bold text-neutral-900 text-[16px] tracking-[-0.005em]">{t.title}</h3>
                <p className="mt-2 text-center text-[13.5px] text-neutral-600 leading-relaxed">{t.body}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
